import React, { useState, useEffect } from 'react'

import { Loader2 } from 'lucide-react'

import amexIcon from '@/assets/images/icons/cards/amex.svg'
import mastercardIcon from '@/assets/images/icons/cards/mastercard.svg'
import visaIcon from '@/assets/images/icons/cards/visa.svg'
import { PaymentToken } from '@/core/api/entities/paymentToken'
import { useUpdatePaymentToken } from '@/core/hooks/payment/useUpdatePaymentToken'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Label } from '@/shared/components/atoms/Label/Label'
import { Switch } from '@/shared/components/atoms/Switch'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

interface EditPaymentMethodModalProps {
	isOpen: boolean
	onClose: () => void
	paymentToken: PaymentToken | null
}

const getCardIcon = (brand: string) => {
	const normalizedBrand = brand.toLowerCase()
	if (normalizedBrand.includes('visa')) {
		return visaIcon
	}
	if (normalizedBrand.includes('mastercard')) {
		return mastercardIcon
	}
	if (
		normalizedBrand.includes('amex') ||
		normalizedBrand.includes('american express')
	) {
		return amexIcon
	}
	return visaIcon
}

const getCardName = (brand: string) => {
	const normalizedBrand = brand.toLowerCase()
	if (normalizedBrand.includes('visa')) {
		return 'Visa'
	}
	if (normalizedBrand.includes('mastercard')) {
		return 'Mastercard'
	}
	if (
		normalizedBrand.includes('amex') ||
		normalizedBrand.includes('american express')
	) {
		return 'American Express'
	}
	return brand
}

export const EditPaymentMethodModal: React.FC<EditPaymentMethodModalProps> = ({
	isOpen,
	onClose,
	paymentToken,
}) => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const [active, setActive] = useState(false)

	const updatePaymentTokenMutation = useUpdatePaymentToken({
		options: {
			onSuccess: () => {
				showNotification({
					type: 'success',
					message:
						t('payment_method.update_success') ||
						'Payment method updated successfully',
				})
				requestAnimationFrame(() => {
					setTimeout(() => {
						onClose()
					}, 2000)
				})
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('payment_method.update_error') || 'Error',
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
			},
		},
	})

	const isLoading = updatePaymentTokenMutation.isPending

	useEffect(() => {
		if (paymentToken) {
			setActive(paymentToken.active)
		}
	}, [paymentToken])

	const handleClose = () => {
		if (isLoading) return
		onClose()
	}

	const handleSave = () => {
		if (!paymentToken) return

		updatePaymentTokenMutation.mutate({
			paymentTokenId: paymentToken.id,
			data: { active },
		})
	}

	if (!paymentToken) return null

	const isCard = paymentToken.type === 'card'
	const cardInfo = paymentToken.paymentInfo?.card
	const sepaInfo = paymentToken.paymentInfo?.sepa

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[452px]">
				<DialogHeader>
					<DialogTitle className="text-lg font-semibold">
						{t('payment_method.edit_title') || 'Edit payment method'}
					</DialogTitle>
				</DialogHeader>

				<div className="border-t border-border"></div>

				<div className="space-y-4">
					{isCard && (
						<div className="flex flex-col gap-2">
							<Label className="text-muted-foreground">
								{t('payment_method.name_on_card')}
							</Label>
							<div className="text-lg text-foreground">John Doe</div>
						</div>
					)}

					{isCard && cardInfo && (
						<div className="flex flex-col gap-2">
							<Label className="text-muted-foreground">
								{t('payment_method.card_number')}
							</Label>
							<div className="flex items-center gap-2">
								<span className="text-lg text-foreground">
									**** **** **** {cardInfo.last4}
								</span>
								<img
									src={getCardIcon(cardInfo.brand)}
									alt={getCardName(cardInfo.brand)}
									className="w-10 h-7 object-contain"
								/>
							</div>
						</div>
					)}

					{!isCard && sepaInfo && (
						<div className="flex flex-col gap-2">
							<Label className="text-muted-foreground">
								{t('payment_method.iban')}
							</Label>
							<div className="text-lg text-foreground">{sepaInfo.iban}</div>
						</div>
					)}

					{isCard && cardInfo && (
						<div className="flex gap-4">
							<div className="flex flex-col gap-2 flex-1">
								<Label className="text-muted-foreground">
									{t('payment_method.expiration_date')}
								</Label>
								<div className="text-lg text-foreground">
									{String(cardInfo.expMonth).padStart(2, '0')}/
									{String(cardInfo.expYear).slice(-2)}
								</div>
							</div>
							<div className="flex flex-col gap-2 flex-1">
								<Label className="text-muted-foreground">
									{t('payment_method.security_code')}
								</Label>
								<div className="text-lg text-foreground">***</div>
							</div>
						</div>
					)}

					<div className="flex items-center gap-3 pt-4 border-t border-border">
						<Switch
							checked={active}
							onCheckedChange={setActive}
							disabled={isLoading}
						/>
						<Label className="text-lg text-foreground">
							{t('payment_method.active_payment_method')}
						</Label>
					</div>

					<div className="flex justify-end gap-3 pt-4">
						<Button
							variant="outline"
							className="rounded-full px-6"
							onClick={handleClose}
							disabled={isLoading}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							className="rounded-full px-6"
							onClick={handleSave}
							disabled={isLoading}
						>
							{isLoading ? (
								<>
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									{t('actions.saving')}
								</>
							) : (
								t('actions.save')
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
